import { BusinessObjectBase } from './base';
import { organizationPropertyValueSchema } from '@schemas/business-objects/organization-property-value';
import type { Organization } from './organization';
import type { OrganizationProperty } from './organization-property';
import type { Id } from '../base-types';

export interface OrganizationPropertyValueData {
  readonly id: Id;
  readonly textValue?: string;
  readonly numberValue?: number;
  readonly organizationId: Id;
  readonly organization?: Organization;
  readonly organizationPropertyId: Id;
  readonly organizationProperty?: OrganizationProperty;
}

export interface OrganizationPropertyValue extends Readonly<OrganizationPropertyValueData> {}
export class OrganizationPropertyValue extends BusinessObjectBase {
  static readonly entityName = 'OrganizationPropertyValue' as const;
  static readonly plural = 'organization-property-values';
  static schema = organizationPropertyValueSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<OrganizationPropertyValue[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new OrganizationPropertyValue(data));
  }

  static async findOne(
    filter?: any,
  ): Promise<OrganizationPropertyValue | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new OrganizationPropertyValue(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
  ): Promise<OrganizationPropertyValue> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new OrganizationPropertyValue(data);
  }

  static async create(
    data: Omit<OrganizationPropertyValueData, 'id'>,
  ): Promise<OrganizationPropertyValue> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new OrganizationPropertyValue(result);
  }

  static async createMany(
    data: Omit<OrganizationPropertyValueData, 'id'>[],
  ): Promise<OrganizationPropertyValue[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new OrganizationPropertyValue(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<OrganizationPropertyValueData>,
  ): Promise<OrganizationPropertyValue> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new OrganizationPropertyValue(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
